/** @format */

import { defineStore } from 'pinia'
import AuthorizedPersonService from '@/api/AuthorizedPersonService.js'
import { useClientStore } from './client'
import { parseISO, formatISO } from 'date-fns'

export const useAuthorizedPersonStore = defineStore('authorizedPerson', {
	state: () => ({
		inputs: {
			id: null,
			first_name: '',
			last_name: '',
			country_id: null,
			date_of_birth: null,
			passport_file_path: '',
			id_verified: false,
			id_verification_date: null,
			archived: false,
			street: '',
			postal_code: '',
			city: '',
			address_country_id: null,
		},
		originalInputs: null,
		saving: false,
		updatingPersonsKycReport: false,
	}),

	actions: {
		clearPerson() {
			this.inputs = {
				id: null,
				first_name: '',
				last_name: '',
				country_id: null,
				date_of_birth: null,
				passport_file_path: '',
				id_verified: false,
				id_verification_date: null,
				archived: false,
				street: '',
				postal_code: '',
				city: '',
				address_country_id: null,
			}
			this.originalInputs = { ...this.inputs }
		},

		_clientStore() {
			return useClientStore()
		},

		updatePersonData(person) {
			this.inputs = {
				id: person.id,
				first_name: person.first_name,
				last_name: person.last_name,
				country_id: person.country_id,
				date_of_birth:
					person.date_of_birth === null ? null : parseISO(person.date_of_birth),
				passport_file_path: person.passport_file_path,
				id_verified: !!person.id_verification_date,
				id_verification_date: person.id_verification_date
					? parseISO(person.id_verification_date)
					: null,
				archived: Boolean(person.archived),
				street: person.address?.street ?? '',
				postal_code: person.address?.postal_code ?? '',
				city: person.address?.city ?? '',
				address_country_id: person.address?.country_id ?? null,
			}
			this.originalInputs = { ...this.inputs }
		},

		async create() {
			this.saving = true

			try {
				const person = await AuthorizedPersonService.create({
					...this.inputs,
					client_id: this._clientStore().clientData.id,
				})
				this.updatePersonData(person)
				this._clientStore().addAuthorizedPerson(person)
			} catch (error) {
				console.error(
					'Error creating authorized person (stores/authorizedPerson/create):',
					error,
				)
			} finally {
				this.saving = false
			}
		},

		async update() {
			this.saving = true

			try {
				const person = await AuthorizedPersonService.update({
					...this.inputs,
					client_id: this._clientStore().clientData.id,
				})
				this.updatePersonData(person)
				this._clientStore().updateAuthorizedPerson(person)
			} catch (error) {
				console.error(
					'Error updating authorized person (stores/authorizedPerson/update):',
					error,
				)
			} finally {
				this.saving = false
			}
		},

		async toggleArchived({ authorizedPerson }) {
			this.updatePersonData(authorizedPerson)
			this.inputs.archived = !authorizedPerson.archived
			await this.update()
		},

		addPassportFilePath(filePath) {
			this.inputs.passport_file_path = filePath
			if (this.originalInputs) {
				this.originalInputs.passport_file_path = filePath
			}
			this._clientStore().updateAuthorizedPerson({
				...this.inputs,
				date_of_birth: formatISO(this.inputs.date_of_birth),
				id_verification_date: this.inputs.id_verification_date
					? formatISO(this.inputs.id_verification_date)
					: null,
			})
		},

		async downloadPassport(authorizedPerson) {
			try {
				await AuthorizedPersonService.downloadPassport(authorizedPerson)
			} catch (error) {
				console.error(
					'Error downloading passport (stores/authorizedPerson/downloadPassport):',
					error,
				)
			}
		},

		async deletePassport() {
			try {
				await AuthorizedPersonService.deletePassport(this.inputs)
				this.inputs.passport_file_path = ''
				if (this.originalInputs) {
					this.originalInputs.passport_file_path = ''
				}
				this._clientStore().updateAuthorizedPerson({
					...this.inputs,
					date_of_birth: formatISO(this.inputs.date_of_birth),
					id_verification_date: this.inputs.id_verification_date
						? formatISO(this.inputs.id_verification_date)
						: null,
				})
				this._clientStore().notification('success', '§ui.notification_delete_successful')
			} catch (error) {
				console.error(
					'Error deleting passport (stores/authorizedPerson/deletePassport):',
					error,
				)
			}
		},

		async resolveRpsMatch(personId, entityIndex) {
			try {
				const response = await AuthorizedPersonService.resolveRpsMatch(
					personId,
					entityIndex,
				)
				return response
			} catch (error) {
				console.error(
					'Error resolving RPS match (stores/authorizedPerson/resolveRpsMatch):',
					error,
				)
				throw error
			}
		},

		async refreshRpsReport(personIds) {
			try {
				this.updatingPersonsKycReport = true
				const response = await AuthorizedPersonService.refreshRpsReport(personIds)
				return response.people
			} catch (error) {
				console.error(
					'Error refreshing RPS report (stores/authorizedPerson/refreshRpsReport):',
					error,
				)
				throw error
			} finally {
				this.updatingPersonsKycReport = false
			}
		},
	},

	getters: {
		isDirty(state) {
			if (state.originalInputs === null) {
				return false
			}

			return JSON.stringify(state.inputs) !== JSON.stringify(state.originalInputs)
		},
	},
})
